import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, ApiError } from '../api-client.js';
import { useAuthStore } from '../auth/auth-store.js';
import {
  clearLegacyLocalDecks,
  createDeck,
  deleteDeck,
  hasLegacyLocalDecks,
  listDecks,
  readLegacyLocalDecks,
} from '../decks/deck-storage.js';
import { getDefaultFormat, getTemplateDecks, type TemplateDeck } from '../decks/formats.js';
import type { DeckRecord } from '../decks/types.js';

export default function DeckListPage() {
  const { gameKey = '' } = useParams();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const [decks, setDecks] = useState<DeckRecord[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [legacyCount, setLegacyCount] = useState(0);
  const [migrating, setMigrating] = useState(false);
  const [busyTemplate, setBusyTemplate] = useState<string | null>(null);
  const templates = getTemplateDecks(gameKey);

  const refresh = useCallback(() => {
    listDecks(gameKey)
      .then(setDecks)
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, [gameKey]);

  useEffect(() => {
    setDecks(null);
    setError(null);
    refresh();
  }, [refresh]);

  // One-time migration banner: count legacy localStorage decks for
  // this game.
  useEffect(() => {
    if (!hasLegacyLocalDecks()) {
      setLegacyCount(0);
      return;
    }
    setLegacyCount(readLegacyLocalDecks(gameKey).length);
  }, [gameKey]);

  let formatName = '';
  let formatKey = '';
  try {
    const fmt = getDefaultFormat(gameKey);
    formatName = fmt.name;
    formatKey = fmt.key;
  } catch {
    // unknown game — handled below
  }

  // Copy a strategy template into the user's saved decks (same path as
  // Duplicate). Suffix the name if one already exists so repeated presses
  // don't pile up identical names.
  async function onAddTemplate(template: TemplateDeck) {
    setError(null);
    setBusyTemplate(template.key);
    try {
      const existing = (decks ?? []).map((d) => d.name.toLowerCase());
      let name = template.name;
      let n = 2;
      while (existing.includes(name.toLowerCase())) name = `${template.name} (${n++})`;
      await createDeck({ gameKey, formatKey, name, entries: template.entries });
      refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyTemplate(null);
    }
  }

  // Play a strategy template straight against the bot — no need to save first
  // (same vs-bot flow the deck builder uses).
  async function onPlayTemplate(template: TemplateDeck) {
    if (!user) {
      navigate('/login', { state: { from: { pathname: `/decks/${gameKey}` } } });
      return;
    }
    setError(null);
    setBusyTemplate(template.key);
    try {
      const created = await api.createVsBotMatch(gameKey, user.id, {
        formatKey,
        entries: template.entries,
      });
      navigate(`/matches/${created.match.id}`);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
      setBusyTemplate(null);
    }
  }

  async function onDelete(id: string, name: string) {
    if (!confirm(`Delete "${name}"? This can't be undone.`)) return;
    try {
      await deleteDeck(gameKey, id);
      refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function onDuplicate(deck: DeckRecord) {
    setError(null);
    try {
      // Suffix " (copy)" / " (copy 2)" / etc. so a button-mash
      // doesn't produce a wall of identically-named decks.
      const baseName = deck.name.replace(/\s*\(copy(?:\s+\d+)?\)\s*$/i, '');
      const existing = (decks ?? []).map((d) => d.name.toLowerCase());
      let copyName = `${baseName} (copy)`;
      let n = 2;
      while (existing.includes(copyName.toLowerCase())) {
        copyName = `${baseName} (copy ${n++})`;
      }
      await createDeck({
        gameKey: deck.gameKey,
        formatKey: deck.formatKey,
        name: copyName,
        entries: deck.entries,
      });
      refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function onMigrate() {
    setMigrating(true);
    setError(null);
    try {
      const local = readLegacyLocalDecks(gameKey);
      for (const deck of local) {
        try {
          await createDeck({
            gameKey: deck.gameKey,
            formatKey: deck.formatKey,
            name: deck.name,
            entries: deck.entries,
          });
        } catch {
          // Skip individual decks that fail validation now (rules
          // may have tightened); leaving them in legacy storage so
          // the user can fix them later if they care.
        }
      }
      // Best-effort: clear local copy across all games once we've
      // tried this one. Future visits won't show the banner.
      clearLegacyLocalDecks();
      setLegacyCount(0);
      refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setMigrating(false);
    }
  }

  if (!formatName) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-ec-oxblood-2">
          No deck-builder format is registered for <code className="font-mono">{gameKey}</code>.
        </p>
        <Link to="/" className="inline-block text-sm text-ec-brass hover:underline">
          ← back to games
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {templates.length > 0 && (
        <section className="space-y-3">
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <h2 className="font-display text-lg font-medium uppercase tracking-wide text-ec-face">Strategy decks</h2>
            <span className="text-sm text-ec-bone-dim">ready-made plans — play one now, or copy it to tweak</span>
          </div>
          <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {templates.map((t) => (
              <li key={t.key} className="ec-surface flex flex-col p-4">
                <div className="flex items-baseline justify-between gap-2">
                  <h3 className="truncate font-display text-base font-medium text-ec-face">{t.name}</h3>
                  <span className="shrink-0 rounded-sm border border-ec-brass/50 bg-ec-ground px-1.5 py-0.5 font-display text-[10px] uppercase tracking-wide text-ec-brass">
                    {t.strategy}
                  </span>
                </div>
                <p className="mt-1 flex-1 text-xs text-ec-bone-dim">{t.description}</p>
                <div className="mt-4 flex gap-2">
                  <button
                    type="button"
                    onClick={() => onPlayTemplate(t)}
                    disabled={busyTemplate !== null}
                    className="ec-btn ec-btn--gold !py-1.5 flex-1 text-sm disabled:opacity-50"
                    title="Start a match against the AI with this deck"
                  >
                    {busyTemplate === t.key ? 'Starting…' : 'Play vs bot'}
                  </button>
                  <button
                    type="button"
                    onClick={() => onAddTemplate(t)}
                    disabled={busyTemplate !== null}
                    className="ec-btn !py-1.5 text-sm disabled:opacity-50"
                    title="Copy this deck into your saved decks so you can edit it"
                  >
                    Add to my decks
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      <header className="flex flex-wrap items-baseline gap-x-6 gap-y-2">
        <h2 className="font-display text-lg font-medium uppercase tracking-wide text-ec-face">My decks</h2>
        <span className="text-sm text-ec-bone-dim">{formatName}</span>
        <Link
          to={`/decks/${gameKey}/new`}
          className="ec-btn ec-btn--gold ml-auto !py-1.5 text-sm"
        >
          + New deck
        </Link>
        <Link
          to={`/library/${gameKey}`}
          className="font-display text-sm uppercase tracking-wide text-ec-brass hover:underline"
        >
          card library →
        </Link>
      </header>

      {legacyCount > 0 && (
        <div className="rounded-md border border-ec-brass/60 bg-[rgba(245,168,28,0.18)] p-3 text-sm">
          <p className="text-ec-brass">
            Found <strong>{legacyCount}</strong> deck{legacyCount === 1 ? '' : 's'} stored in
            this browser before decks were saved to your account. Upload them now to keep
            using them across devices?
          </p>
          <div className="mt-2 flex gap-2">
            <button
              type="button"
              onClick={onMigrate}
              disabled={migrating}
              className="ec-btn ec-btn--primary !py-1.5 text-xs"
            >
              {migrating ? 'Uploading…' : 'Upload to my account'}
            </button>
            <button
              type="button"
              onClick={() => {
                clearLegacyLocalDecks();
                setLegacyCount(0);
              }}
              className="ec-btn !py-1.5 text-xs"
            >
              Discard local copies
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="ec-surface p-3 text-sm text-ec-face">
          {error}
        </div>
      )}

      {error ? null : decks === null ? (
        <p className="text-sm text-ec-bone-dim">Loading your decks…</p>
      ) : decks.length === 0 ? (
        <p className="text-sm text-ec-bone-dim">
          You haven't built any decks for this game yet. Click <strong>+ New deck</strong> to start.
        </p>
      ) : (
        <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {decks.map((deck) => {
            const total = deck.entries.reduce((n, e) => n + e.qty, 0);
            return (
              <li
                key={deck.id}
                className="ec-surface flex flex-col p-4"
              >
                <div className="flex items-baseline justify-between gap-2">
                  <h3 className="truncate font-display text-base font-medium text-ec-face">{deck.name}</h3>
                  <span className="font-mono text-xs text-ec-bone-dim">{total} cards</span>
                </div>
                <p className="mt-1 text-xs text-ec-bone-dim">
                  {deck.formatKey} · updated{' '}
                  {new Date(deck.updatedAt).toLocaleDateString()}
                </p>
                <div className="mt-4 flex gap-2">
                  <Link
                    to={`/decks/${gameKey}/${deck.id}`}
                    className="ec-btn ec-btn--primary !py-1.5 flex-1 text-sm"
                  >
                    Edit
                  </Link>
                  <button
                    type="button"
                    onClick={() => onDuplicate(deck)}
                    className="ec-btn !py-1.5 text-sm"
                    title="Make a copy of this deck"
                  >
                    Duplicate
                  </button>
                  <button
                    type="button"
                    onClick={() => onDelete(deck.id, deck.name)}
                    className="rounded-md border border-ec-oxblood bg-[rgba(39,147,211,0.28)] px-3 py-1.5 font-display text-sm text-ec-face hover:border-ec-oxblood-2"
                  >
                    Delete
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
